import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ConfigurationManagerService } from './configuration-manager.service';
import { ApiService } from './api.service';
import { BulkCreatePayload, MessageWithHeader } from '../interfaces/notification.inerface';

// Add these interfaces if they don't exist in notification.model.ts
export interface NotificationConfig {
  id?: string;
  content: string;
  type?: 'info' | 'success' | 'warning' | 'danger';
  replace?: boolean;
  moveToTop?: boolean;
  autoHide?: boolean;
  duration?: number;
  createdAt?: Date;
  onClick?: () => void;
  onDismiss?: () => void;
  onGoLive?: () => void;
}
interface ParsedNotification {
  name: string;
  description?: string;
  status?: string;
  type?: string;
  created?: string;
  updated?: string;
}

export class Notification {
  public id: string;
  public content: string;
  public type: 'info' | 'success' | 'warning' | 'danger';
  public timestamp: Date;
  public createdAt: string = '';  
  public isVisible: boolean = true;
  public onClick?: () => void;
  public onDismiss?: () => void;
  public onGoLive?: () => void;

  constructor(config: NotificationConfig) {
    this.id = config.id || this.generateId();
    this.content = config.content;
    this.type = config.type || 'info';
    this.timestamp = new Date();
    this.createdAt = config.createdAt ? new Date(config.createdAt).toLocaleString() : '';

    this.onClick = config.onClick;
    this.onDismiss = config.onDismiss;
    this.onGoLive = config.onGoLive;

    // Auto-hide functionality
    if (config.autoHide !== false) {
      const duration = config.duration || 5000;
      setTimeout(() => {
        this.dismiss();
      }, duration);
    }
  }

  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  dismiss(): void {
    this.isVisible = false;
    if (this.onDismiss) {
      this.onDismiss();
    }
  }

  click(): void {
    if (this.onClick) {
      this.onClick();
    }
  }

  executeGoLive(): void {
    if (this.onGoLive) {
      this.onGoLive();
    }
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  // Existing message functionality
  private messageSubject = new BehaviorSubject<string | null>(null);
  message$ = this.messageSubject.asObservable();

  // New notification system
  private notifications = new BehaviorSubject<Notification[]>([]);
  private notificationsMap = new Map<string, Notification>();
  private latestNotificationSubject = new Subject<Notification | null>();

  // Observables for components to subscribe to
  public notifications$ = this.notifications.asObservable();
  public latestNotification$ = this.latestNotificationSubject.asObservable();

  constructor(private http: HttpClient, private api: ApiService) {}

  // ==================== EXISTING MESSAGE FUNCTIONALITY ====================
  
  show(message: string) {
    this.messageSubject.next(message);
    setTimeout(() => this.clear(), 5000); // auto-hide after 5s
  }

  clear() {
    this.messageSubject.next(null);
  }

  async sendBulkMessage(payload: { methods: string[]; subject: string; html: string; text: string; entity: string; userId: string; }, options: {
    incidentId: string;
    perimeterId?: string | null;
    fromOrgId: string;
    fromUserId: string;
    statuses: string[];
    methods: string[];
    subject: string;
    text: string;
    html: string;
    file?: File;
  }): Promise<void> {
    const {
      incidentId,
      perimeterId,
      fromOrgId,
      fromUserId,
      statuses,
      methods,
      subject,
      text: bodyText,
      html: bodyHtml,
      file
    } = options;

    // Determine perimeter filter type
    let perimeterFilterType: 'NONE' | 'PERIMETER' | 'STREET' = 'NONE';
    if (perimeterId) {
      perimeterFilterType = 'PERIMETER';
    } else if (perimeterId === null) {
      perimeterFilterType = 'STREET';
    }

    try {
      // Step 1: Create message header + content
      const messageResponse = await this.createMessageWithHeader(
        fromUserId,
        methods,
        subject,
        bodyHtml,
        bodyText
      );
      const messageId = messageResponse.data.id;

      // Step 2: Create message headers to incident users
      const headerParams = {
        body: {
          messageId,
          incidentId,
          statuses,
          perimeterFilterType,
          perimeterId,
          fromOrganizationId: fromOrgId
        }
      };

      const headersResult = await this.requestToBackend(
        'bulkCreateMessageHeadersToIncidentUsers',
        headerParams
      );

      // Step 3: Upload attachment if present
      if (headersResult.data.count > 0 && file) {
        await this.createMessageAttachment(file, messageId);
      }
      
      // Step 4: Send the message
      await this.sendMessageFromOrganization(messageId);
    } catch (error) {
      console.error('Error sending bulk message:', error);
    }
    return;
  }

  sendSMSandEMAIL(payload: MessageWithHeader): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.Message;
    const body = {
        methods: payload.methods,
        subject: payload.subject,
        html: payload.html,
        text: payload.text,
        entity: payload.entity,
        userId: payload.userId
    };
    return this.api.post<any>(url, body);
  }

  public createMessageWithHeader(userId: string, methods: string[], subject: string, html: string, text: string) {
    const param = {
      body: {
        methods,
        subject,
        html,
        text,
        entity: 'USER',
        userId
      }
    };
    return this.requestToBackend('createMessageWithHeader', param);
  }
  ///  for sending the message attachment 
  public createMessageAttachment(file: File, messageId: string): Observable<any> {
  const url = `${ConfigurationManagerService.settings.BaseURL}` + `${ConfigurationManagerService.settings.CreateMessageAttachment}`;
  // Create form data
  const formData = new FormData();
  formData.append('file', file);
  formData.append('messageId', messageId);
  return this.api.post(url, formData);
}

  public sendMessageFromOrganization(messageId: string): Observable<any>  {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.sendFromOrganization +`/${messageId}/sendFromOrganization`;
  
    return this.http.get<any>(url)
  }

   bulkCreateToUsersAndOrganizations(payload: BulkCreatePayload,messageId: string): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.BulkCreateToUsersAndOrganizations;
    const body = {
        messageId: messageId,
        toUserIds: payload.toUserIds,
        fromOrganizationId: payload.fromOrganizationId,
    };
    return this.api.post<any>(url, body);
  }

  private requestToBackend(endpoint: string, payload: any) {
    // Customize this as per your backend API endpoint
    const url = `/api/${endpoint}`;
    return this.http.post<any>(url, payload).toPromise();
  }

  // sendOrgMessage(options: any): any {
  //   throw new Error('Method not implemented.');
  // }
  getSMS(payload: BulkCreatePayload,messageId: string): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.BulkCreateToUsersAndOrganizations;
    const body = {
        messageId: messageId,
        toUserIds: payload.toUserIds,
        fromOrganizationId: payload.fromOrganizationId,
    };
    return this.api.post<any>(url, body);
  }


  // ==================== NEW NOTIFICATION SYSTEM ====================

  // Add a notification
  addNotification(config: NotificationConfig): Notification {
    const notification = new Notification(config);
    
    // Handle replacement logic
    if (config.id && (config.replace || !this.notificationsMap.has(config.id))) {
      this.deleteNotification(config.id);
    } else if (config.id && this.notificationsMap.has(config.id)) {
      return this.notificationsMap.get(config.id)!;
    }

    // Add to map and list
    this.notificationsMap.set(notification.id, notification);
    const currentNotifications = this.notifications.value;
    currentNotifications.push(notification);
    this.notifications.next([...currentNotifications]);

    // Handle moveToTop - show as latest notification
    if (config.moveToTop) {
      this.latestNotificationSubject.next(notification);
      
      // Auto-hide latest notification after 3 seconds
      setTimeout(() => {
        this.latestNotificationSubject.next(null);
      }, 3000);
    }

    return notification;
  }

  // Convenience methods for different notification types
  addInfoNotification(content: string, config: Partial<NotificationConfig> = {}): void {
    this.addNotification({ ...config, content, type: 'info' });
  }

  addDangerNotification(content: string, config: Partial<NotificationConfig> = {}): void {
    this.addNotification({ ...config, content, type: 'danger' });
  }

  addWarningNotification(content: string, config: Partial<NotificationConfig> = {}): void {
    this.addNotification({ ...config, content, type: 'warning' });
  }

  addSuccessNotification(content: string, config: Partial<NotificationConfig> = {}): void {
    this.addNotification({ ...config, content, type: 'success' });
  }

  // Get all notifications
  getNotifications(): Notification[] {
    return this.notifications.value;
  }

  // Get specific notification
  getNotification(id: string): Notification | undefined {
    return this.notificationsMap.get(id);
  }

  // Get notification count
  getNotificationCount(): number {
    return this.notifications.value.length;
  }

  // Delete specific notification
  deleteNotification(id: string): void {
    const notification = this.notificationsMap.get(id);
    if (notification) {
      this.notificationsMap.delete(id);
      const currentNotifications = this.notifications.value;
      const updatedNotifications = currentNotifications.filter(n => n.id !== id);
      this.notifications.next(updatedNotifications);
    }
  }

  // Clear all notifications
  clearNotifications(): void {
    // Trigger dismiss events for all notifications
    this.notifications.value.forEach(notification => {
      notification.dismiss();
    });
    
    this.notificationsMap.clear();
    this.notifications.next([]);
  }

  // Clear all notifications immediately
  clearAll(): void {
    this.notifications.next([]);
  }

  // Clear notifications by type
  clearByType(type: 'info' | 'success' | 'warning' | 'danger'): void {
    const currentNotifications = this.notifications.value;
    const filteredNotifications = currentNotifications.filter(n => n.type !== type);
    this.notifications.next(filteredNotifications);
  }

  // Clear notifications older than specified minutes
  clearOlderThan(minutes: number): void {
    const cutoffTime = new Date(Date.now() - minutes * 60 * 1000);
    const currentNotifications = this.notifications.value;
    const filteredNotifications = currentNotifications.filter(n => n.timestamp >= cutoffTime);
    this.notifications.next(filteredNotifications);
  }

  // Get notifications by type
  getNotificationsByType(type: 'info' | 'success' | 'warning' | 'danger'): Notification[] {
    return this.notifications.value.filter(n => n.type === type);
  }

  // Handle notification dismissed
  notificationDismissed(id: string): void {
    const notification = this.notificationsMap.get(id);
    if (notification) {
      notification.dismiss();
      this.deleteNotification(id);
    }
  }

  // Handle notification clicked
  notificationClicked(id: string): void {
    const notification = this.notificationsMap.get(id);
    if (notification) {
      notification.click();
    }
  }

  // Handle go live action
  goLiveToEvent(id: string): void {
    const notification = this.notificationsMap.get(id);
    if (notification) {
      notification.executeGoLive();
    }
  }
}